/**
 * useCroSupply — V2.K.1
 *
 * Fetches CRO supply data from CoinGecko :
 *  - circulating_supply : tokens actually freely tradable
 *  - total_supply       : all tokens that exist on-chain (including
 *                         the Strategic Reserve escrow locked under
 *                         CIP-006 — vesting linearly until ~2035)
 *  - max_supply         : hard cap (100 B per Cronos whitepaper)
 *
 * The "locked" figure exposed below is a derived value
 * (`total - circulating`). It captures everything currently held
 * off-market, dominated by the CIP-006 Strategic Reserve escrow
 * (~70 B CRO, unlocking ~1.167 B / month over 60 months).
 *
 * Refresh : every 5 minutes. CoinGecko updates supply numbers a few
 * times a day, so polling more often is wasteful.
 */

import { useQuery } from '@tanstack/react-query';

export interface CroSupplyData {
  circulating: number;
  total: number;
  max: number;
  locked: number;
  /** % of total supply currently in circulation. */
  floatPct: number;
  /** % of total supply still locked (mainly Strategic Reserve). */
  lockedPct: number;
  /** % of max supply already minted. */
  mintedPct: number;
  /** ISO timestamp at which CoinGecko refreshed the figures. */
  lastUpdated: string | null;
}

async function fetchCroSupply(): Promise<CroSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/crypto-com-chain?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || 0;
  // Cronos hard cap per whitepaper. CoinGecko returns 100 000 000 000.
  const max = Number(md.max_supply) || 100_000_000_000;
  const locked = Math.max(0, total - circulating);

  return {
    circulating,
    total,
    max,
    locked,
    floatPct: total > 0 ? (circulating / total) * 100 : 0,
    lockedPct: total > 0 ? (locked / total) * 100 : 0,
    mintedPct: max > 0 ? (total / max) * 100 : 0,
    lastUpdated: json.last_updated || null,
  };
}

export function useCroSupply() {
  return useQuery({
    queryKey: ['cro', 'supply'],
    queryFn: fetchCroSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
